import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Table, Button, Spinner, Alert } from "react-bootstrap";
import { FiUsers, FiArrowLeft, FiRotateCcw, FiTrash2 } from "react-icons/fi";
import api from "../api/axiosConfig";

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const DeletedUsers = () => {
  const navigate = useNavigate();
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [restoringId, setRestoringId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchDeletedUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/api/admin/deleted-users");
      setDeletedUsers(response.data || []);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to fetch deleted users list."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeletedUsers();
  }, [fetchDeletedUsers]);

  const handleRestore = async (deletedId) => {
    setRestoringId(deletedId);
    setError("");
    try {
      await api.put(`/api/admin/deleted-users/${deletedId}/restore`);
      setDeletedUsers((prev) =>
        prev.filter((entry) => entry.deletedId !== deletedId)
      );
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to restore the selected user."
      );
    } finally {
      setRestoringId(null);
    }
  };

  const handlePermanentDelete = async (deletedId) => {
    if (
      !window.confirm(
        "This will permanently remove the user snapshot. Continue?"
      )
    ) {
      return;
    }

    setDeletingId(deletedId);
    setError("");
    try {
      await api.delete(`/api/admin/deleted-users/${deletedId}`);
      setDeletedUsers((prev) =>
        prev.filter((entry) => entry.deletedId !== deletedId)
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to permanently delete the selected user."
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="manage-users-page">
      <Container>
        <div className="page-header">
          <h2 className="page-title">
            <FiUsers /> Deleted Users
          </h2>
          <div style={{ display: "flex", gap: "10px" }}>
            <Button
              variant="outline-secondary"
              className="back-button"
              onClick={() => navigate("/manage-users")}
              type="button"
            >
              <FiArrowLeft /> Back to Manage Users
            </Button>
            <Button
              variant="primary"
              onClick={fetchDeletedUsers}
              disabled={loading}
              type="button"
            >
              Refresh
            </Button>
          </div>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" />
          </div>
        ) : deletedUsers.length === 0 ? (
          <Alert variant="info">No deleted users found.</Alert>
        ) : (
          <div className="table-responsive">
            <Table hover className="manage-users-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Deleted On</th>
                  <th>Purge On</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deletedUsers.map((entry) => (
                  <tr key={entry.deletedId}>
                    <td>{entry.userId}</td>
                    <td>{entry.user?.name || "-"}</td>
                    <td>{entry.user?.username || "-"}</td>
                    <td>{entry.user?.email || "-"}</td>
                    <td>{entry.role?.replace(/_/g, " ")}</td>
                    <td>{formatDate(entry.deletedAt)}</td>
                    <td>{formatDate(entry.purgeAfter)}</td>
                    <td className="text-center">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          size="sm"
                          variant="success"
                          disabled={restoringId === entry.deletedId}
                          onClick={() => handleRestore(entry.deletedId)}
                        >
                          {restoringId === entry.deletedId ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Restoring...
                            </>
                          ) : (
                            <>
                              <FiRotateCcw /> Restore
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={deletingId === entry.deletedId}
                          onClick={() => handlePermanentDelete(entry.deletedId)}
                        >
                          {deletingId === entry.deletedId ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <FiTrash2 /> Delete
                            </>
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Container>
    </div>
  );
};

export default DeletedUsers;
